import React, { useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { useOnClickOutside } from 'usehooks-ts';

import { transfromRewardsToCommonFormat } from '@/common/lib/common';
import { convertRemToPixels } from '@/common/lib/convertRemtoPx';
import { getImage } from '@/common/lib/images';
import { getText } from '@/common/lib/text';
import { type FactoryTypeText } from '@/common/types/factory';
import { type GoodsType } from '@/common/types/goods';
import { type PlantTypeText } from '@/common/types/plant';
import { type ResourceTypeText } from '@/common/types/resource';
import { type LevelUpSocketEventType } from '@/common/types/socket';
import { type UnionGameItemType } from '@/common/types/userType';
import { createRequirementLink } from '@/components/RequirementsItem/requirement-link';
import {
  selectBuildLevelByName,
  selectNextStepPlant,
} from '@/redux/selectors/userSelectors';
import { useAppSelector } from '@/redux/store';

import { LevelUpRewardItem } from './LevelUpRewardItem';

import arrowSvg from '/img/icons/arrow-go.svg';
import checkSvg from '/img/icons/check.svg';
import chestsSvg from '/img/icons/chests.svg';
import iPng from '/img/icons/i.png';
import lockSvg from '/img/icons/lock-icon.svg';

interface prop {
  id: string;
  name: string;
  level: number;
  type: string;
  reward?: LevelUpSocketEventType;
  complete: boolean;
}

const StepPlantCard: React.FC<prop> = React.memo(
  ({ id, name, level, reward, complete }) => {
    const { t } = useTranslation();
    const navigate = useNavigate();
    const plantLevel = useAppSelector((state) =>
      selectBuildLevelByName(state, name as UnionGameItemType),
    );
    const rewardRef = useRef<HTMLDivElement | null>(null);

    const selectNextStep = useAppSelector(selectNextStepPlant);
    const [chestReward, setChestReward] = useState(false);

    useOnClickOutside(rewardRef, () => {
      setChestReward(false);
    });

    const image = useMemo(
      () =>
        getImage({
          name: name as string,
          field: 'build',
        }),
      [name],
    );

    const nameFactory = useMemo(
      () =>
        getText({
          name: name as string,
          kind: 'build',
          t,
        }),
      [name, t],
    );

    const activeCard = selectNextStep?.id === id;

    const getIcon = useMemo((): string => {
      return complete ? checkSvg : activeCard ? arrowSvg : lockSvg;
    }, [complete, activeCard]);

    const navigateItem = (): void => {
      const link = createRequirementLink({
        value: level,
        type: 'plant',
        name: name as
          | GoodsType
          | PlantTypeText
          | ResourceTypeText
          | FactoryTypeText,
        plantLevel: plantLevel || 0,
      });

      if (link) {
        navigate(link, { state: { name } });
      }
    };

    const allRewards = transfromRewardsToCommonFormat([
      reward?.wallet,
      reward?.materials,
      reward?.lottery,
    ]);

    return (
      <div
        className="nextLevel-steps-card"
        key={id}
        onClick={() => activeCard && navigateItem()}
      >
        <div
          className={`nextLevel-check-block ${complete ? 'received' : activeCard ? 'active' : ''}`}
        ></div>
        <div
          className="additionalInformation-NextLevel"
          style={{
            opacity: complete ? '0.7' : '1',
          }}
        >
          <div className="w-full h-full absolute top-0 left-0 overflow-hidden">
            {activeCard && <div className="colorSegment"></div>}
          </div>

          <div style={{ display: 'flex', gap: '0.5rem' }}>
            <picture>
              <source srcSet={image?.webp} type="image/webp" />
              <img
                src={image?.img}
                alt="icon"
                width={convertRemToPixels(2.25)}
              />
            </picture>
            <ul className="additionalInformation-NextLevel-text">
              <li>
                {`${
                  activeCard
                    ? `${t('Current Task:')} ${level > 1 ? t('Upgrade') : t('Build')}`
                    : t(level > 1 ? 'Upgrade' : 'Build')
                }`}
              </li>
              <li>{`${nameFactory} ${level > 1 ? t('to Level') + ' ' + level : ''}`}</li>
            </ul>
          </div>
          {reward ? (
            <div ref={rewardRef}>
              <div
                className="additionalInformation-chest"
                onClickCapture={(event) => {
                  event.stopPropagation();
                  setChestReward((state) => !state);
                }}
              >
                <img
                  src={chestsSvg}
                  alt="icon"
                  width={convertRemToPixels(2.25)}
                />
                <img
                  src={iPng}
                  className="i"
                  alt="icon"
                  width={convertRemToPixels(1)}
                />
              </div>
              {chestReward && (
                <ul
                  onClickCapture={(event) => event.stopPropagation()}
                  className="additionalInformation-NextLevel-rewards"
                >
                  {allRewards.map(({ item, value }) => (
                    <LevelUpRewardItem
                      key={item}
                      itemName={item}
                      value={value}
                    />
                  ))}
                </ul>
              )}
            </div>
          ) : (
            <img src={getIcon} alt="icon" width={convertRemToPixels(1.25)} />
          )}
        </div>
      </div>
    );
  },
);

export default StepPlantCard;
